import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Collapse,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TimelineIcon from '@mui/icons-material/Timeline';
import QrCodeIcon from '@mui/icons-material/QrCode';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BuildIcon from '@mui/icons-material/Build';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SearchIcon from '@mui/icons-material/Search';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import EventIcon from '@mui/icons-material/Event';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SyncIcon from '@mui/icons-material/Sync';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import useAppTheme from 'hooks/useAppTheme';
import { useAppSelector } from 'store/hook';
import { type Asset } from 'types/asset.types';
import { type preferredTimeDisplayModeTypes } from 'types/customer.types';
import TireInventoryHandler from 'handlers/tire-inventory.handler';
import { ThemePalette } from 'mui.theme';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import {
  TIRE_EVENT_CATEGORIES,
  type TireEventCategory,
  type TireTimelineEvent,
  type TireTimelineResponse,
} from 'types/tire-timeline.types';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import { convertCentToFahr } from 'utils/helpers/unitSystem';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import TireAIPredictionModal from './TireAIPredictionModal';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

dayjs.extend(utc);

// ─── Category config ─────────────────────────────────────────────────────────
const categoryConfig: Record<
  TireEventCategory,
  { color: string; icon: React.ReactNode; label: string }
> = {
  LIFECYCLE: {
    color: '#2196f3',
    icon: <SwapVertIcon sx={{ fontSize: 18 }} />,
    label: 'Lifecycle',
  },
  // TELEMETRY: {
  //   color: '#00bcd4',
  //   icon: <SensorsIcon sx={{ fontSize: 18 }} />,
  //   label: 'Telemetry',
  // },
  INSPECTION: {
    color: '#ff9800',
    icon: <SearchIcon sx={{ fontSize: 18 }} />,
    label: 'Inspection',
  },
  ALERT: {
    color: '#f44336',
    icon: <NotificationsActiveIcon sx={{ fontSize: 18 }} />,
    label: 'Alert',
  },
  MAINTENANCE: {
    color: '#9c27b0',
    icon: <BuildIcon sx={{ fontSize: 18 }} />,
    label: 'Maintenance',
  },
};

const severityIcon: Record<string, React.ReactNode> = {
  info: <InfoOutlinedIcon sx={{ fontSize: 16, color: '#2196f3' }} />,
  warning: <WarningAmberIcon sx={{ fontSize: 16, color: '#ff9800' }} />,
  error: <ErrorOutlineIcon sx={{ fontSize: 16, color: '#f44336' }} />,
  critical: <ErrorOutlineIcon sx={{ fontSize: 16, color: '#d32f2f' }} />,
};

// ─── Telemetry Sparkline ─────────────────────────────────────────────────────
interface TelemetryChartProps {
  telemetry: TireTimelineEvent['telemetry'];
  enableDarkTheme: boolean;
  preferredTimeDisplayMode: preferredTimeDisplayModeTypes;
  customerTimezone: string;
  eventTimestamp?: string;
  isAlert?: boolean;
  eventType?: string;
}

const TelemetryChart = ({
  telemetry,
  enableDarkTheme,
  preferredTimeDisplayMode,
  customerTimezone,
  eventTimestamp,
  isAlert,
  eventType = '',
}: TelemetryChartProps) => {
  const [blinkOn, setBlinkOn] = useState(true);
  const unitSystem =
    useAppSelector((state) => state.authReducer.customer?.unitSystem) ??
    'metric';
  const isImperial = unitSystem !== 'metric';

  useEffect(() => {
    if (!isAlert) return;
    const id = setInterval(() => {
      setBlinkOn((v) => !v);
    }, 550);
    return () => {
      clearInterval(id);
    };
  }, [isAlert]);

  if (!telemetry || telemetry.length === 0) return null;

  const data = telemetry.map((t) => ({
    time: convertUtcToDisplayTime(
      t.timestamp,
      preferredTimeDisplayMode,
      customerTimezone,
      'hh:mm A'
    ),
    // Always parse as UTC to avoid browser local-time interpretation of strings without Z
    _rawMs: dayjs.utc(t.timestamp).valueOf(),
    pressure: t.pressure,
    temperature: isImperial ? convertCentToFahr(t.temperature) : t.temperature,
  }));

  // Find the index of the data point closest to eventTimestamp
  let alertIndex = -1;
  if (isAlert && eventTimestamp) {
    const eventMs = dayjs.utc(eventTimestamp).valueOf();
    let closestDiff = Infinity;
    data.forEach((d, i) => {
      const diff = Math.abs(d._rawMs - eventMs);
      if (diff < closestDiff) {
        closestDiff = diff;
        alertIndex = i;
      }
    });
  }

  // Decide which line(s) to mark based on eventType
  const typeUpper = eventType.toUpperCase();
  const alertLine: 'pressure' | 'temperature' | 'both' =
    typeUpper.includes('TEMP') || typeUpper.includes('TEMPERATURE')
      ? 'temperature'
      : typeUpper.includes('PRESSURE') || typeUpper.includes('PSI')
      ? 'pressure'
      : 'both';

  const pressureUnit = telemetry[0]?.pressureUnit ?? 'psi';
  const tempUnit = isImperial ? '°F' : '°C';
  const textColor = enableDarkTheme
    ? 'rgba(255,255,255,0.6)'
    : 'rgba(0,0,0,0.55)';

  // Returns alert-dot SVG for the given line color and data index
  const alertDotSvg = (
    color: string,
    cx: number,
    cy: number,
    index: number
  ) => {
    if (index !== alertIndex) return <g key={`dot-skip-${index}`} />;
    return (
      <g key={`alert-dot-${index}`}>
        <circle
          cx={cx}
          cy={cy}
          r={blinkOn ? 14 : 8}
          fill={color}
          opacity={blinkOn ? 0.25 : 0}
          style={{ transition: 'r 0.3s ease, opacity 0.3s ease' }}
        />
        <circle
          cx={cx}
          cy={cy}
          r={6}
          fill={color}
          stroke="#fff"
          strokeWidth={2}
          opacity={blinkOn ? 1 : 0.2}
          style={{ transition: 'opacity 0.3s ease' }}
        />
      </g>
    );
  };

  const renderPressureDot = ({
    cx = 0,
    cy = 0,
    index = -1,
  }: {
    cx?: number;
    cy?: number;
    index?: number;
  }) => alertDotSvg('#2196f3', cx, cy, index);

  const renderTempDot = ({
    cx = 0,
    cy = 0,
    index = -1,
  }: {
    cx?: number;
    cy?: number;
    index?: number;
  }) => alertDotSvg('#ff9800', cx, cy, index);

  return (
    <Box sx={{ mt: 2, width: '100%' }}>
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={data}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={
              enableDarkTheme ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'
            }
          />
          <XAxis
            dataKey="time"
            tick={{ fill: textColor, fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            yAxisId="pressure"
            tick={{ fill: textColor, fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            width={45}
            label={{
              value: pressureUnit,
              angle: -90,
              position: 'insideLeft',
              style: { fill: textColor, fontSize: 10 },
            }}
          />
          <YAxis
            yAxisId="temp"
            orientation="right"
            tick={{ fill: textColor, fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            width={40}
            label={{
              value: tempUnit,
              angle: 90,
              position: 'insideRight',
              style: { fill: textColor, fontSize: 10 },
            }}
          />
          <RechartsTooltip
            contentStyle={{
              backgroundColor: enableDarkTheme ? '#1e1e2f' : '#fff',
              border: enableDarkTheme
                ? '1px solid rgba(255,255,255,0.15)'
                : '1px solid rgba(0,0,0,0.1)',
              borderRadius: '8px',
              color: enableDarkTheme ? '#fff' : '#000',
              fontSize: 12,
            }}
          />
          <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
          <Line
            yAxisId="pressure"
            type="monotone"
            dataKey="pressure"
            stroke="#2196f3"
            strokeWidth={2}
            dot={
              isAlert && alertIndex >= 0 && alertLine !== 'temperature'
                ? renderPressureDot
                : false
            }
            activeDot={{ r: 4 }}
            name={`Pressure (${pressureUnit})`}
          />
          <Line
            yAxisId="temp"
            type="monotone"
            dataKey="temperature"
            stroke="#ff9800"
            strokeWidth={2}
            dot={
              isAlert && alertIndex >= 0 && alertLine !== 'pressure'
                ? renderTempDot
                : false
            }
            activeDot={{ r: 4 }}
            name={`Temperature (${tempUnit})`}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

// ─── Event Card ──────────────────────────────────────────────────────────────
const EventCard = ({
  event,
  enableDarkTheme,
  isLast,
  assets,
  preferredTimeDisplayMode,
  customerTimezone,
}: {
  event: TireTimelineEvent;
  enableDarkTheme: boolean;
  isLast: boolean;
  assets: Asset[];
  preferredTimeDisplayMode: preferredTimeDisplayModeTypes;
  customerTimezone: string;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const [chartOpen, setChartOpen] = useState(false);
  // Helper: resolve assetId to bumperNumber
  const resolveBumperNumber = (id: string) => {
    const asset = assets.find((a) => a.assetId === id);
    return asset?.bumperNumber ?? id;
  };
  const cat = categoryConfig[event.eventCategory] ?? categoryConfig.LIFECYCLE;
  const cardBg = enableDarkTheme
    ? palette.boxBackground
    : palette.boxBackground;
  const borderColor = enableDarkTheme
    ? 'rgba(255,255,255,0.12)'
    : 'rgba(0,0,0,0.1)';
  const textPrimary = palette.textPrimary;
  const textSecondary = enableDarkTheme
    ? 'rgba(255,255,255,0.55)'
    : 'rgba(0,0,0,0.55)';

  const hasMetadata = event.metadata && Object.keys(event.metadata).length > 0;

  return (
    <Box sx={{ display: 'flex', gap: 2.5, position: 'relative' }}>
      {/* ── Timeline connector ── */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
          width: 40,
          pt: 0.5,
        }}
      >
        {/* Dot */}
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: `${cat.color}22`,
            border: `2px solid ${cat.color}`,
            color: cat.color,
            flexShrink: 0,
            zIndex: 1,
          }}
        >
          {cat.icon}
        </Box>
        {/* Vertical line */}
        {!isLast && (
          <Box
            sx={{
              width: 2,
              flex: 1,
              backgroundColor: borderColor,
              mt: 0.5,
            }}
          />
        )}
      </Box>

      {/* ── Card ── */}
      <Box
        sx={{
          flex: 1,
          backgroundColor: cardBg,
          borderRadius: '12px',
          border: `1px solid ${borderColor}`,
          p: 2.5,
          mb: isLast ? 0 : 3,
          transition: 'border-color 0.2s',
          '&:hover': {
            borderColor: cat.color,
          },
        }}
      >
        {/* Header row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            mb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {severityIcon[event.severity] ?? severityIcon.info}
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: textPrimary, lineHeight: 1.3 }}
            >
              {event.title}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
            <Chip
              label={cat.label}
              size="small"
              sx={{
                backgroundColor: `${cat.color}22`,
                color: cat.color,
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 22,
                border: `1px solid ${cat.color}44`,
              }}
            />
            <Chip
              label={event.eventType.replace(/_/g, ' ')}
              size="small"
              variant="outlined"
              sx={{
                fontWeight: 500,
                fontSize: '0.65rem',
                height: 22,
                borderColor,
                color: textSecondary,
              }}
            />
          </Box>
        </Box>

        {/* Timestamp */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <EventIcon sx={{ fontSize: 14, color: textSecondary }} />
          <Typography variant="caption" sx={{ color: textSecondary }}>
            {convertUtcToDisplayTime(
              event.eventTimestamp,
              preferredTimeDisplayMode,
              customerTimezone,
              'MMM DD, YYYY hh:mm A'
            )}
          </Typography>
        </Box>

        {/* Metadata */}
        {(hasMetadata || event.eventType === 'TIRE_ONBOARDED') && (
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              flexWrap: 'wrap',
              mt: 1,
            }}
          >
            {/* Explicitly show Tire Serial Number for onboarding events */}
            {event.eventType === 'TIRE_ONBOARDED' && event.tireSerialNumber && (
              <Box
                sx={{
                  backgroundColor: enableDarkTheme
                    ? 'rgba(255,255,255,0.05)'
                    : 'rgba(0,0,0,0.03)',
                  borderRadius: '6px',
                  px: 1.2,
                  py: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: textSecondary,
                    fontSize: '0.65rem',
                    display: 'block',
                  }}
                >
                  {t('tire_serial_number')}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: textPrimary,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                  }}
                >
                  {event.tireSerialNumber}
                </Typography>
              </Box>
            )}

            {hasMetadata &&
              (() => {
                const entries: Array<[string, any]> = [];
                const ignoredKeys = new Set([
                  'tireSerialNumber',
                  'serialNumber',
                  'serial_number',
                  'serial',
                  'eventDate',
                  'date',
                  'timestamp',
                  'eventTimestamp',
                  'eventType',
                  'eventCategory',
                  'customerId',
                  'createdBy',
                  'id',
                  'resolvedAssetId',
                  'userCustomerId',
                  'userId',
                  'user_id',
                  'createdAt',
                  'updatedAt',
                ]);

                Object.entries(event.metadata ?? {}).forEach(([key, value]) => {
                  if (
                    key === 'rawRow' &&
                    typeof value === 'object' &&
                    value !== null
                  ) {
                    Object.entries(value).forEach(
                      ([nestedKey, nestedValue]) => {
                        if (!ignoredKeys.has(nestedKey)) {
                          entries.push([nestedKey, nestedValue]);
                        }
                      }
                    );
                  } else {
                    if (!ignoredKeys.has(key)) {
                      entries.push([key, value]);
                    }
                  }
                });

                return entries
                  .filter(([, value]) => {
                    if (value === null || value === undefined || value === '')
                      return false;
                    // Hide raw S3 URIs — they're shown as images/PDF buttons below
                    if (typeof value === 'string') {
                      if (
                        value.startsWith('s3://') ||
                        /https?:\/\/[^"'\s]*s3[^"'\s]*amazonaws\.com/i.test(
                          value
                        ) ||
                        /https?:\/\/[^"'\s]*\.s3\./i.test(value)
                      )
                        return false;
                    }
                    return true;
                  })
                  .map(([key, value]) => {
                    const displayKey =
                      key === 'assetId'
                        ? 'Bumper ID'
                        : key.replace(/([A-Z])/g, ' $1').trim();

                    // Detect timestamp fields by key name and convert to customer tz
                    const isTimestampKey = /(?:Ts|Timestamp|At|Time)$/i.test(
                      key
                    );
                    const isObject =
                      typeof value === 'object' && value !== null;
                    const rawStr = isObject
                      ? JSON.stringify(value)
                      : String(value);
                    const parsedMs = Date.parse(rawStr);
                    const isValidDate =
                      !isObject && isTimestampKey && !isNaN(parsedMs);

                    const displayValue =
                      key === 'assetId'
                        ? resolveBumperNumber(rawStr)
                        : isObject
                        ? Object.entries(value)
                            .map(
                              ([k, v]) =>
                                `${k}: ${
                                  typeof v === 'object' ? JSON.stringify(v) : v
                                }`
                            )
                            .join(', ')
                        : isValidDate
                        ? convertUtcToDisplayTime(
                            rawStr,
                            preferredTimeDisplayMode,
                            customerTimezone,
                            'MMM DD, YYYY hh:mm A'
                          )
                        : rawStr;

                    return (
                      <Box
                        key={key}
                        sx={{
                          backgroundColor: enableDarkTheme
                            ? 'rgba(255,255,255,0.05)'
                            : 'rgba(0,0,0,0.03)',
                          borderRadius: '6px',
                          px: 1.2,
                          py: 0.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: textSecondary,
                            fontSize: '0.65rem',
                            display: 'block',
                          }}
                        >
                          {displayKey}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: textPrimary,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        >
                          {displayValue}
                        </Typography>
                      </Box>
                    );
                  });
              })()}
          </Box>
        )}

        {/* S3 media viewer — images / PDFs from inspection metadata */}
        {event.eventCategory === 'INSPECTION' &&
          (() => {
            const isS3Uri = (v: unknown): v is string => {
              if (typeof v !== 'string') return false;
              return (
                v.startsWith('s3://') ||
                /https?:\/\/[^"'\s]*s3[^"'\s]*amazonaws\.com/i.test(v) ||
                /https?:\/\/[^"'\s]*\.s3\./i.test(v)
              );
            };

            const isImageUrl = (url: string) =>
              /\.(jpg|jpeg|png|webp|gif|bmp|svg)(\?.*)?$/i.test(url);
            const isPdfUrl = (url: string) => /\.pdf(\?.*)?$/i.test(url);

            // Collect all S3 URIs from metadata values (can be nested one level)
            const mediaUrls: Array<{
              url: string;
              type: 'image' | 'pdf' | 'other';
            }> = [];
            const scanValue = (v: unknown) => {
              if (isS3Uri(v)) {
                mediaUrls.push({
                  url: v,
                  type: isImageUrl(v) ? 'image' : isPdfUrl(v) ? 'pdf' : 'other',
                });
              }
            };
            Object.values(event.metadata ?? {}).forEach(scanValue);

            if (mediaUrls.length === 0) return null;

            const images = mediaUrls.filter((m) => m.type === 'image');
            const pdfs = mediaUrls.filter((m) => m.type === 'pdf');
            const others = mediaUrls.filter((m) => m.type === 'other');

            return (
              <Box sx={{ mt: 1.5 }}>
                {/* Images */}
                {images.length > 0 && (
                  <Box
                    sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}
                  >
                    {images.map((m, idx) => (
                      <Box
                        key={idx}
                        component="a"
                        href={m.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'block',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          border: enableDarkTheme
                            ? '1px solid rgba(255,255,255,0.12)'
                            : '1px solid rgba(0,0,0,0.1)',
                          flexShrink: 0,
                          width: 220,
                          height: 165,
                          cursor: 'pointer',
                          transition: 'transform 0.15s ease',
                          '&:hover': { transform: 'scale(1.04)' },
                        }}
                      >
                        <Box
                          component="img"
                          src={m.url}
                          alt={`inspection-${idx + 1}`}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}

                {/* PDFs */}
                {pdfs.map((m, idx) => (
                  <Box
                    key={`pdf-${idx}`}
                    component="a"
                    href={m.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.8,
                      px: 1.5,
                      py: 0.6,
                      mb: 0.5,
                      mr: 0.5,
                      borderRadius: '6px',
                      backgroundColor: enableDarkTheme
                        ? 'rgba(244,67,54,0.12)'
                        : 'rgba(244,67,54,0.07)',
                      border: '1px solid rgba(244,67,54,0.35)',
                      color: '#f44336',
                      textDecoration: 'none',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      transition: 'background-color 0.15s ease',
                      '&:hover': {
                        backgroundColor: enableDarkTheme
                          ? 'rgba(244,67,54,0.22)'
                          : 'rgba(244,67,54,0.14)',
                      },
                    }}
                  >
                    {t('open_pdf')} {pdfs.length > 1 ? `${idx + 1}` : ''}
                  </Box>
                ))}

                {/* Other S3 files */}
                {others.map((m, idx) => (
                  <Box
                    key={`file-${idx}`}
                    component="a"
                    href={m.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.8,
                      px: 1.5,
                      py: 0.6,
                      mb: 0.5,
                      mr: 0.5,
                      borderRadius: '6px',
                      backgroundColor: enableDarkTheme
                        ? 'rgba(255,255,255,0.06)'
                        : 'rgba(0,0,0,0.04)',
                      border: enableDarkTheme
                        ? '1px solid rgba(255,255,255,0.15)'
                        : '1px solid rgba(0,0,0,0.12)',
                      color: textPrimary,
                      textDecoration: 'none',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: enableDarkTheme
                          ? 'rgba(255,255,255,0.1)'
                          : 'rgba(0,0,0,0.08)',
                      },
                    }}
                  >
                    {t('open_file')} {others.length > 1 ? `${idx + 1}` : ''}
                  </Box>
                ))}
              </Box>
            );
          })()}

        {/* Telemetry chart — collapsible accordion */}
        {event.telemetry && event.telemetry.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Box
              onClick={() => {
                setChartOpen((v) => !v);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
                cursor: 'pointer',
                userSelect: 'none',
                py: 0.6,
                px: 1,
                borderRadius: '8px',
                width: 'fit-content',
                transition: 'background-color 0.15s',
                '&:hover': {
                  backgroundColor: enableDarkTheme
                    ? 'rgba(255,255,255,0.06)'
                    : 'rgba(0,0,0,0.04)',
                },
              }}
            >
              <ShowChartIcon sx={{ fontSize: 16, color: '#2196f3' }} />
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: '#2196f3', fontSize: '0.75rem' }}
              >
                {t('telemetry_graph')}
              </Typography>
              {chartOpen ? (
                <KeyboardArrowUpIcon sx={{ fontSize: 16, color: '#2196f3' }} />
              ) : (
                <KeyboardArrowDownIcon
                  sx={{ fontSize: 16, color: '#2196f3' }}
                />
              )}
            </Box>
            <Collapse in={chartOpen} timeout={300}>
              <TelemetryChart
                telemetry={event.telemetry}
                enableDarkTheme={enableDarkTheme}
                preferredTimeDisplayMode={preferredTimeDisplayMode}
                customerTimezone={customerTimezone}
                eventTimestamp={event.eventTimestamp}
                isAlert={event.eventCategory === 'ALERT'}
                eventType={event.eventType}
              />
            </Collapse>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const TimelineDetail = () => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { preferredTimeDisplayMode, timezoneBasedOnDisplayMode } =
    useTimeDisplayMode();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const customerId = useAppSelector(
    (state) => state.persistedReducer.customer.id
  );
  const assets = useAppSelector((state) => state.assetReducer.assets);

  // Params
  const serialNumber = searchParams.get('serialNumber');
  const assetId = searchParams.get('assetId');
  const position = searchParams.get('position');

  const mode: 'serial' | 'asset' | 'unknown' = serialNumber
    ? 'serial'
    : assetId && position
    ? 'asset'
    : 'unknown';

  // State
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [timelineData, setTimelineData] = useState<TireTimelineResponse | null>(
    null
  );
  const [activeCategories, setActiveCategories] = useState<TireEventCategory[]>(
    [...TIRE_EVENT_CATEGORIES]
  );
  const [jumpDate, setJumpDate] = useState('');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const pageTopRef = useRef<HTMLDivElement>(null);
  const [scrollParent, setScrollParent] = useState<HTMLElement | undefined>(
    undefined
  );
  const [showAIPrediction, setShowAIPrediction] = useState(false);

  // Pagination states
  const [events, setEvents] = useState<TireTimelineEvent[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 20;

  useEffect(() => {
    if (!pageTopRef.current) return;
    let parent = pageTopRef.current.parentElement;
    while (parent) {
      const style = window.getComputedStyle(parent);
      if (
        style.overflowY === 'auto' ||
        style.overflowY === 'scroll' ||
        style.overflowY === 'overlay'
      ) {
        setScrollParent(parent);
        return;
      }
      parent = parent.parentElement;
    }
  }, []);

  // Resolve assetId -> bumperNumber for header chip
  const assetBumperNumber = useMemo(() => {
    if (!assetId) return assetId;
    const asset = assets.find((a) => a.assetId === assetId);
    return asset?.bumperNumber ?? assetId;
  }, [assetId, assets]);

  // Filtered and sorted events (sorted on server-side now)
  const filteredEvents = events;

  // Jump to date handler
  const handleJumpToDate = useCallback(
    (dateStr: string) => {
      setJumpDate(dateStr);
      if (!dateStr || !virtuosoRef.current || filteredEvents.length === 0)
        return;

      const targetDate = new Date(dateStr).getTime();
      let closestIndex = -1;
      let closestDiff = Infinity;

      filteredEvents.forEach((event, index) => {
        const cardDate = new Date(event.eventTimestamp).getTime();
        const diff = Math.abs(cardDate - targetDate);
        if (diff < closestDiff) {
          closestDiff = diff;
          closestIndex = index;
        }
      });

      if (closestIndex !== -1) {
        virtuosoRef.current.scrollToIndex({
          index: closestIndex,
          align: 'center',
          behavior: 'smooth',
        });
      }
    },
    [filteredEvents]
  );

  const tireInventoryHandler = new TireInventoryHandler();

  // Reset pagination when category filters or sort order changes
  useEffect(() => {
    setOffset(0);
    setEvents([]);
    setHasMore(true);
  }, [activeCategories, sortOrder]);

  // Fetch timeline page
  useEffect(() => {
    const fetchTimelinePage = async () => {
      if (mode === 'unknown') return;

      setIsLoading(offset === 0);
      const payload = {
        ...(mode === 'serial'
          ? { tireSerialNumber: serialNumber ?? '' }
          : { assetId: assetId ?? '', position: position ?? '' }),
        forceSync: false,
        categories: activeCategories,
        sortOrder,
        limit: PAGE_SIZE,
        offset,
      };

      const result = await tireInventoryHandler.getTimeline(
        customerId,
        payload
      );

      if (result) {
        setEvents((prev) =>
          offset === 0 ? result.rows : [...prev, ...result.rows]
        );
        setTimelineData(result);
        setHasMore(offset + result.rows.length < result.count);
      }
      setIsLoading(false);
    };

    void fetchTimelinePage();
  }, [
    customerId,
    mode,
    serialNumber,
    assetId,
    position,
    activeCategories,
    sortOrder,
    offset,
  ]);

  // Force sync handler
  const handleForceSync = async () => {
    setShowSyncDialog(false);
    if (mode === 'unknown') return;

    setIsSyncing(true);
    setEvents([]);
    setOffset(0);
    setHasMore(true);

    const payload = {
      ...(mode === 'serial'
        ? { tireSerialNumber: serialNumber ?? '' }
        : { assetId: assetId ?? '', position: position ?? '' }),
      forceSync: true,
      categories: activeCategories,
      sortOrder,
      limit: PAGE_SIZE,
      offset: 0,
    };

    const result = await tireInventoryHandler.getTimeline(customerId, payload);
    if (result) {
      setEvents(result.rows);
      setTimelineData(result);
      setHasMore(result.rows.length < result.count);
    }
    setIsSyncing(false);
  };

  // Export PDF handler
  const handleExportPdf = async () => {
    if (mode === 'unknown') return;

    setIsExporting(true);
    const payload =
      mode === 'serial'
        ? { tireSerialNumber: serialNumber ?? '', forceSync: false }
        : {
            assetId: assetId ?? '',
            position: position ?? '',
            forceSync: false,
          };

    const blob = await tireInventoryHandler.exportTimelinePdf(
      customerId,
      payload
    );
    if (blob) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filename = `tire_timeline_${assetId ?? serialNumber}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
    setIsExporting(false);
  };

  // Human-readable label for how long ago the last event occurred
  const lastEventAgo = useMemo(() => {
    if (!timelineData?.rows || timelineData.rows.length === 0) return null;
    const latestMs = Math.max(
      ...timelineData.rows.map((e) => dayjs.utc(e.eventTimestamp).valueOf())
    );
    const nowMs = Date.now();
    const diffMs = nowMs - latestMs;
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor(diffMs / (1000 * 60));
    if (days >= 2) return `${days} days ago`;
    if (days === 1) return `yesterday`;
    if (hours >= 1) return `${hours}h ago`;
    if (minutes >= 1) return `${minutes}m ago`;
    return 'just now';
  }, [timelineData]);

  // Derive active installation for AI Prediction button
  // In asset mode: always available from URL params
  // In serial mode: scan events for the most recent one with assetId+position metadata
  //                 whose eventType is not a removal/uninstall
  const activeInstallation = useMemo<{
    assetId: string;
    position: string;
  } | null>(() => {
    if (mode === 'asset' && assetId && position) {
      return { assetId, position };
    }
    if (mode === 'serial' && events.length > 0) {
      const sorted = [...events].sort(
        (a, b) =>
          new Date(b.eventTimestamp).getTime() -
          new Date(a.eventTimestamp).getTime()
      );
      const installEvent = sorted.find(
        (e) =>
          e.metadata?.assetId &&
          e.metadata?.position &&
          !e.eventType.toUpperCase().includes('UNINSTALL') &&
          !e.eventType.toUpperCase().includes('REMOV')
      );
      if (installEvent) {
        return {
          assetId: installEvent.metadata.assetId as string,
          position: installEvent.metadata.position as string,
        };
      }
    }
    return null;
  }, [mode, assetId, position, events]);

  // Toggle category filter
  const handleCategoryToggle = (cat: TireEventCategory) => {
    setActiveCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  // ── Palette ────────────────────────────────────────────────────────────────
  const bg = enableDarkTheme ? palette.pageBackground : palette.pageBackground;
  const cardBg = enableDarkTheme
    ? palette.boxBackground
    : palette.boxBackground;
  const borderColor = enableDarkTheme
    ? 'rgba(255,255,255,0.12)'
    : 'rgba(0,0,0,0.1)';
  const textPrimary = palette.textPrimary;
  const textSecondary = enableDarkTheme
    ? 'rgba(255,255,255,0.55)'
    : 'rgba(0,0,0,0.55)';

  return (
    <CustomMuiThemeProvider>
      <Box
        ref={pageTopRef}
        sx={{ p: 3, backgroundColor: bg, minHeight: '100%' }}
      >
        {/* ── Header ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <Tooltip title={t('back_to_timeline')}>
            <IconButton
              onClick={() => {
                navigate(-1);
              }}
              sx={{
                color: textPrimary,
                border: `1px solid ${borderColor}`,
                borderRadius: '8px',
                p: 0.8,
                '&:hover': {
                  backgroundColor: enableDarkTheme
                    ? 'rgba(255,255,255,0.08)'
                    : 'rgba(0,0,0,0.05)',
                },
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <TimelineIcon
            sx={{ color: ThemePalette.typography.primary, fontSize: 26 }}
          />
          <Typography variant="h5" sx={{ fontWeight: 700, color: textPrimary }}>
            {t('tire_timeline')}
          </Typography>

          <Chip
            icon={
              mode === 'serial' ? (
                <QrCodeIcon sx={{ fontSize: 14 }} />
              ) : (
                <DirectionsCarIcon sx={{ fontSize: 14 }} />
              )
            }
            label={
              mode === 'serial'
                ? `Serial: ${serialNumber}`
                : `${assetBumperNumber} • ${position}`
            }
            size="small"
            sx={{
              backgroundColor:
                mode === 'serial'
                  ? 'rgba(33,150,243,0.15)'
                  : 'rgba(76,175,80,0.15)',
              color: mode === 'serial' ? '#2196f3' : '#4caf50',
              fontWeight: 600,
              border: `1px solid ${
                mode === 'serial'
                  ? 'rgba(33,150,243,0.3)'
                  : 'rgba(76,175,80,0.3)'
              }`,
            }}
          />
        </Box>

        {/* ── Toolbar (STICKY) ── */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backdropFilter: 'blur(16px)',
            backgroundColor: enableDarkTheme
              ? 'rgba(15,15,25,0.82)'
              : 'rgba(245,245,250,0.88)',
            borderRadius: '12px',
            mx: -3,
            px: 3,
            py: 1.5,
            borderBottom: `1px solid ${borderColor}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            mb: 3,
          }}
        >
          {/* ── LEFT: Category filter pills ── */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.6,
              backgroundColor: enableDarkTheme
                ? 'rgba(255,255,255,0.05)'
                : 'rgba(0,0,0,0.04)',
              border: `1px solid ${borderColor}`,
              borderRadius: '14px',
              p: '5px 8px',
              flexWrap: 'wrap',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: textSecondary,
                fontWeight: 700,
                letterSpacing: 1,
                textTransform: 'uppercase',
                fontSize: '0.6rem',
                mr: 0.5,
                userSelect: 'none',
              }}
            >
              Filter
            </Typography>
            {TIRE_EVENT_CATEGORIES.map((cat) => {
              const cfg = categoryConfig[cat];
              const isActive = activeCategories.includes(cat);
              return (
                <Box
                  key={cat}
                  onClick={() => {
                    handleCategoryToggle(cat);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1.2,
                    py: 0.4,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    color: isActive ? cfg.color : textSecondary,
                    backgroundColor: isActive
                      ? `${cfg.color}1a`
                      : 'transparent',
                    boxShadow: isActive
                      ? `0 0 0 1.5px ${cfg.color}55, inset 0 0 8px ${cfg.color}18`
                      : 'none',
                    transition: 'all 0.18s ease',
                    opacity: isActive ? 1 : 0.45,
                    '&:hover': {
                      opacity: 1,
                      backgroundColor: `${cfg.color}12`,
                    },
                    '& svg': { fontSize: '14px !important' },
                  }}
                >
                  {cfg.icon}
                  {cfg.label}
                </Box>
              );
            })}
          </Box>

          {/* Sort Order Toggle */}
          <Box
            onClick={() => {
              setSortOrder((prev) => (prev === 'newest' ? 'oldest' : 'newest'));
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.4,
              height: 34,
              borderRadius: '10px',
              cursor: 'pointer',
              userSelect: 'none',
              fontWeight: 700,
              fontSize: '0.72rem',
              color: ThemePalette.typography.primary,
              backgroundColor: `${ThemePalette.typography.primary}12`,
              border: `1.5px solid ${ThemePalette.typography.primary}44`,
              transition: 'all 0.18s ease',
              '&:hover': {
                backgroundColor: `${ThemePalette.typography.primary}22`,
                border: `1.5px solid ${ThemePalette.typography.primary}88`,
              },
              '& svg': { fontSize: '16px !important' },
            }}
          >
            <SwapVertIcon />
            <Typography
              sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'inherit' }}
            >
              {sortOrder === 'newest' ? 'Newest' : 'Oldest'}
            </Typography>
          </Box>

          {/* Jump to Date */}
          <TextField
            type="date"
            size="small"
            label={t('jump_to_date')}
            value={jumpDate}
            onChange={(e) => {
              handleJumpToDate(e.target.value);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{
              minWidth: 160,
              '& .MuiOutlinedInput-root': {
                color: textPrimary,
                borderRadius: '10px',
                height: 34,
                fontSize: '0.72rem',
                '& fieldset': { borderColor },
                '&:hover fieldset': {
                  borderColor: ThemePalette.typography.primary,
                },
              },
              '& .MuiInputLabel-root': {
                color: textSecondary,
                fontSize: '0.72rem',
              },
              '& input': {
                color: textPrimary,
                py: '6px',
                '&::-webkit-calendar-picker-indicator': {
                  filter: enableDarkTheme ? 'invert(1)' : 'none',
                },
              },
            }}
          />

          {/* ── Spacer ── */}
          <Box sx={{ flex: 1 }} />

          {/* ── RIGHT: Action buttons ── */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Export PDF */}
            <Tooltip title={isExporting ? 'Exporting...' : 'Export PDF'} arrow>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  disabled={isExporting || isLoading}
                  startIcon={
                    isExporting ? (
                      <CircularProgress size={14} sx={{ color: 'inherit' }} />
                    ) : (
                      <PictureAsPdfIcon />
                    )
                  }
                  onClick={() => {
                    void handleExportPdf();
                  }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    borderRadius: '10px',
                    px: 2,
                    py: 0.85,
                    backgroundColor: ThemePalette.typography.primary,
                    color: '#fff',
                    boxShadow: `0 3px 10px ${ThemePalette.typography.primary}55`,
                    '&:hover': {
                      backgroundColor: ThemePalette.typography.primary,
                      boxShadow: `0 4px 16px ${ThemePalette.typography.primary}77`,
                      transform: 'translateY(-1px)',
                    },
                    '&:active': { transform: 'scale(0.97)' },
                    '&.Mui-disabled': { opacity: 0.45 },
                    transition: 'all 0.18s ease',
                  }}
                >
                  {isExporting ? 'Exporting…' : 'PDF'}
                </Button>
              </span>
            </Tooltip>

            {/* Sync */}
            <Tooltip title={isSyncing ? 'Syncing…' : 'Sync Latest'} arrow>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  disabled={isSyncing || isLoading}
                  startIcon={
                    <SyncIcon
                      sx={{
                        animation: isSyncing
                          ? 'spin 1s linear infinite'
                          : 'none',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' },
                        },
                      }}
                    />
                  }
                  onClick={() => {
                    setShowSyncDialog(true);
                  }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    borderRadius: '10px',
                    px: 2,
                    py: 0.85,
                    backgroundColor: ThemePalette.typography.primary,
                    color: '#fff',
                    boxShadow: `0 3px 10px ${ThemePalette.typography.primary}55`,
                    '&:hover': {
                      backgroundColor: ThemePalette.typography.primary,
                      boxShadow: `0 4px 16px ${ThemePalette.typography.primary}77`,
                      transform: 'translateY(-1px)',
                    },
                    '&:active': { transform: 'scale(0.97)' },
                    '&.Mui-disabled': { opacity: 0.45 },
                    transition: 'all 0.18s ease',
                  }}
                >
                  {isSyncing ? 'Syncing…' : 'SYNC'}
                </Button>
              </span>
            </Tooltip>

            {/* AI Prediction */}
            {activeInstallation != null && (
              <Tooltip title={t('ai_tire_health_prediction')} arrow>
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={isLoading}
                    startIcon={
                      <AutoAwesomeIcon
                        sx={{
                          '@keyframes starBlink': {
                            '0%, 100%': {
                              transform: 'scale(1)',
                              filter: 'drop-shadow(0 0 0px #fff)',
                              opacity: 1,
                            },
                            '50%': {
                              transform: 'scale(1.35)',
                              filter: 'drop-shadow(0 0 4px #fff)',
                              opacity: 0.75,
                            },
                          },
                          animation: 'starBlink 1.8s ease-in-out infinite',
                        }}
                      />
                    }
                    onClick={() => {
                      setShowAIPrediction(true);
                    }}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      borderRadius: '10px',
                      px: 2,
                      py: 0.85,
                      backgroundColor: ThemePalette.typography.primary,
                      color: '#fff',
                      boxShadow: `0 3px 10px ${ThemePalette.typography.primary}55`,
                      '&:hover': {
                        backgroundColor: ThemePalette.typography.primary,
                        boxShadow: `0 4px 16px ${ThemePalette.typography.primary}77`,
                        transform: 'translateY(-1px)',
                      },
                      '&:active': { transform: 'scale(0.97)' },
                      '&.Mui-disabled': { opacity: 0.45 },
                      transition: 'all 0.18s ease',
                    }}
                  >
                    {t('ai_prediction')}
                    <Box
                      component="span"
                      sx={{
                        ml: 1,
                        fontSize: '0.55rem',
                        fontWeight: 800,
                        letterSpacing: '0.06em',
                        backgroundColor: 'rgba(255,255,255,0.22)',
                        borderRadius: '4px',
                        px: '5px',
                        py: '1px',
                        verticalAlign: 'middle',
                      }}
                    >
                      BETA
                    </Box>
                  </Button>
                </span>
              </Tooltip>
            )}

            {/* Jump to Top */}
            <Tooltip title={t('back_to_top')} arrow>
              <IconButton
                size="small"
                onClick={() => {
                  pageTopRef.current?.scrollIntoView({ behavior: 'smooth' });
                }}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  backgroundColor: ThemePalette.typography.primary,
                  color: '#fff',
                  boxShadow: `0 3px 10px ${ThemePalette.typography.primary}55`,
                  '&:hover': {
                    backgroundColor: ThemePalette.typography.primary,
                    boxShadow: `0 4px 16px ${ThemePalette.typography.primary}77`,
                    transform: 'translateY(-1px)',
                  },
                  '&:active': { transform: 'scale(0.97)' },
                  transition: 'all 0.18s ease',
                  '& svg': { fontSize: '18px !important' },
                }}
              >
                <KeyboardArrowUpIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* ── Content ── */}
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 10,
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: ThemePalette.typography.primary }} />
            <Typography variant="body2" sx={{ color: textSecondary }}>
              {t('loading_timeline_events')}
            </Typography>
          </Box>
        ) : filteredEvents.length === 0 ? (
          <Box
            sx={{
              backgroundColor: cardBg,
              borderRadius: '12px',
              border: `1px dashed ${borderColor}`,
              p: 6,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <TimelineIcon
              sx={{
                fontSize: 60,
                color: ThemePalette.typography.primary,
                opacity: 0.4,
              }}
            />
            <Typography
              variant="h6"
              sx={{ color: textPrimary, fontWeight: 600 }}
            >
              {timelineData
                ? 'No events match the selected filters'
                : 'No timeline events found'}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: textSecondary, textAlign: 'center' }}
            >
              {timelineData
                ? 'Try enabling more categories.'
                : 'There are no events recorded for this tire yet.'}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                navigate(-1);
              }}
              sx={{
                mt: 1,
                textTransform: 'none',
                fontWeight: 600,
                color: textPrimary,
                borderColor,
                '&:hover': {
                  borderColor: ThemePalette.typography.primary,
                  color: ThemePalette.typography.primary,
                },
              }}
            >
              {t('back_to_timeline')}
            </Button>
          </Box>
        ) : (
          <Box>
            {/* Sync info banner */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                backgroundColor: enableDarkTheme
                  ? 'rgba(255,152,0,0.08)'
                  : 'rgba(255,152,0,0.06)',
                border: `1px solid ${
                  enableDarkTheme
                    ? 'rgba(255,152,0,0.25)'
                    : 'rgba(255,152,0,0.2)'
                }`,
                borderRadius: '10px',
                px: 2,
                py: 1,
                mb: 2,
              }}
            >
              <SyncIcon sx={{ fontSize: 18, color: '#ff9800' }} />
              <Typography
                variant="caption"
                sx={{ color: textSecondary, fontSize: '0.8rem' }}
              >
                {lastEventAgo ? (
                  <>
                    {t('last_event_happened')}{' '}
                    <strong style={{ color: textPrimary }}>
                      {lastEventAgo}
                    </strong>
                    {' — click '}
                    <strong style={{ color: '#ff9800' }}>
                      {t('sync_latest')}
                    </strong>
                    {' to fetch up-to-date data.'}
                  </>
                ) : (
                  <>
                    Click{' '}
                    <strong style={{ color: '#ff9800' }}>
                      {t('sync_latest')}
                    </strong>
                    {' to fetch the most current data.'}
                  </>
                )}
              </Typography>
            </Box>

            {/* Results summary */}
            <Typography variant="body2" sx={{ color: textSecondary, mb: 2 }}>
              Showing {filteredEvents.length} of {timelineData?.count ?? 0}{' '}
              events
            </Typography>

            {/* ── Vertical Timeline ── */}
            <Box sx={{ pl: 1 }}>
              <Virtuoso
                useWindowScroll={!scrollParent}
                customScrollParent={scrollParent ?? undefined}
                overscan={600}
                ref={virtuosoRef}
                data={filteredEvents}
                endReached={() => {
                  if (hasMore && !isLoading && !isSyncing) {
                    setOffset((prev) => prev + PAGE_SIZE);
                  }
                }}
                itemContent={(index, event) => (
                  <Box data-event-date={event.eventTimestamp}>
                    <EventCard
                      event={event}
                      enableDarkTheme={enableDarkTheme}
                      isLast={index === filteredEvents.length - 1}
                      assets={assets}
                      preferredTimeDisplayMode={preferredTimeDisplayMode}
                      customerTimezone={timezoneBasedOnDisplayMode ?? 'UTC'}
                    />
                  </Box>
                )}
              />
              {isLoading && offset > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress
                    size={24}
                    sx={{ color: ThemePalette.typography.primary }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        )}
        <Dialog
          open={showSyncDialog}
          onClose={() => {
            setShowSyncDialog(false);
          }}
          PaperProps={{
            sx: {
              backgroundColor: cardBg,
              color: textPrimary,
              borderRadius: '12px',
              border: `1px solid ${borderColor}`,
            },
          }}
        >
          <DialogContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <SyncIcon sx={{ color: '#ff9800', fontSize: 24 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {t('sync_with_latest_data')}
              </Typography>
            </Box>
            <DialogContentText sx={{ color: textSecondary }}>
              {t(
                'this_will_re_fetch_all_timeline_events_with_the_latest_data_'
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => {
                setShowSyncDialog(false);
              }}
              sx={{
                textTransform: 'none',
                color: textSecondary,
              }}
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={() => {
                void handleForceSync();
              }}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: '#ff9800',
                '&:hover': { backgroundColor: '#f57c00' },
              }}
            >
              {t('sync_now')}
            </Button>
          </DialogActions>
        </Dialog>

        {activeInstallation != null && (
          <TireAIPredictionModal
            open={showAIPrediction}
            onClose={() => {
              setShowAIPrediction(false);
            }}
            customerId={customerId}
            assetId={activeInstallation.assetId}
            tirePosition={activeInstallation.position}
            tireSerialNumber={serialNumber ?? undefined}
          />
        )}
      </Box>
    </CustomMuiThemeProvider>
  );
};

export default TimelineDetail;
